import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Link as LinkIcon, Copy, Trash2, Plus, Calendar, Users2, Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const INSTITUTION_TYPES = [
  { id: "universidad", label: "Universidad" },
  { id: "hospital", label: "Hospital / centro sanitario" },
  { id: "clinica", label: "Clínica privada" },
  { id: "entidad_social", label: "Entidad / asociación social" },
  { id: "investigacion", label: "Centro de investigación" },
  { id: "particular", label: "Uso particular" },
  { id: "otro", label: "Otro" },
];

const STATUS_STYLE = {
  active: "bg-primary/15 text-primary",
  revoked: "bg-destructive/15 text-destructive",
  expired: "bg-warning/20 text-foreground",
  exhausted: "bg-muted text-muted-foreground",
};

const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

export default function AdminInvitations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    institution: "", institution_type: "universidad",
    allowed_role: "patient", auto_approve: true,
    max_uses: "", expires_days: "", note: "",
    recipient_email: "",
  });
  const [lastCreated, setLastCreated] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/invitations");
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.institution.trim()) { toast.error("Introduce la entidad"); return; }
    setSaving(true);
    const payload = {
      institution: form.institution.trim(),
      institution_type: form.institution_type,
      allowed_role: form.allowed_role,
      auto_approve: form.auto_approve,
      note: form.note.trim() || null,
    };
    if (form.max_uses) payload.max_uses = Number(form.max_uses);
    if (form.expires_days) {
      const d = new Date();
      d.setDate(d.getDate() + Number(form.expires_days));
      payload.expires_at = d.toISOString();
    }
    if (form.recipient_email) payload.recipient_email = form.recipient_email.trim();
    try {
      const { data } = await api.post("/admin/invitations", payload);
      setLastCreated(data);
      setOpen(false);
      setForm({ institution: "", institution_type: "universidad", allowed_role: "patient", auto_approve: true, max_uses: "", expires_days: "", note: "", recipient_email: "" });
      if (data.email_result?.status === "sent") toast.success("Invitación creada y email enviado");
      else if (data.email_result?.status === "logged") toast.success("Invitación creada (email pendiente: configura RESEND_API_KEY)");
      else toast.success("Invitación creada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear"); }
    finally { setSaving(false); }
  };

  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(inviteUrl(token)); toast.success("Enlace copiado al portapapeles"); }
    catch { toast.error("No se pudo copiar"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta invitación? Los enlaces existentes dejarán de funcionar.")) return;
    try { await api.post(`/admin/invitations/${id}/revoke`); toast.success("Invitación revocada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-invitations-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">Administración</p>
          <h1 className="heading text-4xl font-extrabold mt-1">Invitaciones por entidad</h1>
          <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
            Genera un enlace único para que universidades, clínicas o entidades sociales registren a sus usuarios
            ya autorizados y con la entidad preasignada.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full h-11 px-5" data-testid="new-invite-btn">
          <Plus size={16} className="mr-1" /> Nueva invitación
        </Button>
      </div>

      {lastCreated && (
        <Card className="rounded-2xl border-primary/40 bg-primary/5 pop-in" data-testid="invite-created-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="text-primary shrink-0 mt-1" size={22} />
              <div className="flex-1 min-w-0">
                <div className="heading font-semibold">Enlace de invitación listo para compartir</div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Entidad: <b>{lastCreated.institution}</b> · Rol: <b>{lastCreated.allowed_role}</b>
                </p>
                <div className="mt-3 flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm break-all">
                  <LinkIcon size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1" data-testid="invite-link-text">{inviteUrl(lastCreated.token)}</span>
                  <Button size="sm" variant="outline" onClick={() => copyLink(lastCreated.token)} className="rounded-full" data-testid="copy-invite-link">
                    <Copy size={14} className="mr-1" /> Copiar
                  </Button>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setLastCreated(null)}>Cerrar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-border">
        <CardHeader>
          <CardTitle className="heading">Invitaciones existentes</CardTitle>
          <CardDescription>Copia el enlace para compartir, revisa cuántas veces se ha usado o revócalo.</CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center border border-dashed border-border rounded-xl">
              Aún no has creado ninguna invitación.
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => (
                <li key={it.id} className="border border-border rounded-xl p-4" data-testid={`invite-row-${it.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="heading font-bold flex items-center gap-1"><Building2 size={14} /> {it.institution}</span>
                        <Badge variant="outline" className="capitalize">{it.institution_type.replace("_"," ")}</Badge>
                        <Badge variant="outline" className="capitalize">Rol: {it.allowed_role}</Badge>
                        {it.auto_approve && <Badge className="bg-success/20 text-success">Auto-aprobar</Badge>}
                        <Badge className={STATUS_STYLE[it.status]}>{it.status}</Badge>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Users2 size={13} /> Usos: {it.uses_count}{it.max_uses ? `/${it.max_uses}` : ""}</span>
                        <span className="flex items-center gap-1">Registrados: <b>{it.users_registered ?? 0}</b></span>
                        <span className="flex items-center gap-1">Activos: <b>{it.users_active ?? 0}</b></span>
                        {it.avg_pre_accuracy != null && (
                          <span className="flex items-center gap-1">Precisión PRE media: <b>{Math.round(it.avg_pre_accuracy * 100)}%</b></span>
                        )}
                        {it.expires_at && <span className="flex items-center gap-1"><Calendar size={13} /> Caduca: {new Date(it.expires_at).toLocaleDateString()}</span>}
                      </div>
                      {it.note && <p className="text-sm italic text-muted-foreground mt-1">"{it.note}"</p>}
                      <div className="mt-3 flex items-center gap-2 bg-secondary/40 border border-border rounded-lg px-3 py-2 font-mono text-xs break-all">
                        <LinkIcon size={12} />
                        <span className="flex-1">{inviteUrl(it.token)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="bg-white p-2 rounded-lg border border-border" data-testid={`qr-${it.id}`}>
                        <QRCodeSVG value={inviteUrl(it.token)} size={96} level="M" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => copyLink(it.token)} className="rounded-full" data-testid={`copy-btn-${it.id}`}>
                          <Copy size={14} className="mr-1" /> Copiar
                        </Button>
                        {it.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => revoke(it.id)}
                            className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"
                            data-testid={`revoke-btn-${it.id}`}>
                            <Trash2 size={14} className="mr-1" /> Revocar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva invitación</DialogTitle>
            <DialogDescription>Define entidad, rol y límites. Se generará un enlace único.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la entidad</Label>
              <Input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })}
                placeholder="Ej: Universidad de Salamanca" className="mt-2 h-11" data-testid="new-inv-institution" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.institution_type} onValueChange={(v) => setForm({ ...form, institution_type: v })}>
                  <SelectTrigger className="mt-2 h-11" data-testid="new-inv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{INSTITUTION_TYPES.map(i => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rol asignado</Label>
                <Select value={form.allowed_role} onValueChange={(v) => setForm({ ...form, allowed_role: v })}>
                  <SelectTrigger className="mt-2 h-11" data-testid="new-inv-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Paciente</SelectItem>
                    <SelectItem value="professional">Profesional</SelectItem>
                    <SelectItem value="researcher">Investigador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Máximo usos (opcional)</Label>
                <Input type="number" min="1" value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="mt-2 h-11" placeholder="Sin límite" data-testid="new-inv-max" />
              </div>
              <div>
                <Label>Caduca en (días)</Label>
                <Input type="number" min="1" value={form.expires_days}
                  onChange={(e) => setForm({ ...form, expires_days: e.target.value })}
                  className="mt-2 h-11" placeholder="Sin caducidad" data-testid="new-inv-days" />
              </div>
            </div>
            <div>
              <Label>Nota interna (opcional)</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Ej: Cohorte 2026 · Máster en Neuropsicología" data-testid="new-inv-note" />
            </div>
            <div>
              <Label>Email del invitado (opcional)</Label>
              <Input type="email" value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                placeholder="Si lo indicas, le enviamos el enlace por correo"
                className="mt-2 h-11" data-testid="new-inv-recipient" />
            </div>
            <div>
              <Label className="text-base">Tipo de acceso</Label>
              <p className="text-xs text-muted-foreground mb-2">Elige cómo entrará quien use el enlace:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, auto_approve: true })}
                  data-testid="new-inv-mode-direct"
                  className={`text-left rounded-2xl border p-4 transition-colors ${form.auto_approve ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                  <div className="flex items-center gap-2 heading font-bold">
                    <span className={`inline-block w-3 h-3 rounded-full ${form.auto_approve ? "bg-primary" : "bg-muted"}`} />
                    Acceso directo
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Ya aprobado por ti. Entra al instante sin espera.</p>
                </button>
                <button type="button" onClick={() => setForm({ ...form, auto_approve: false })}
                  data-testid="new-inv-mode-review"
                  className={`text-left rounded-2xl border p-4 transition-colors ${!form.auto_approve ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                  <div className="flex items-center gap-2 heading font-bold">
                    <span className={`inline-block w-3 h-3 rounded-full ${!form.auto_approve ? "bg-primary" : "bg-muted"}`} />
                    Solicitud para revisión
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Se envía como pendiente y tú apruebas después.</p>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={create} disabled={saving} className="rounded-full" data-testid="create-invite-btn">
              {saving ? "Creando..." : "Crear invitación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
